import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Empty, Layout, Space, Spin, Typography } from 'antd';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import HeaderBar from '../../Layout/HeaderBar';
import FooterBar from '../../Layout/FooterBar';
import { assistantRequest } from '../PipedriveAssistant/api';

const taskSchema = z.object({
  id: z.string(),
  subject: z.string(),
  customerName: z.string(),
  note: z.string(),
  dueDate: z.string(),
});
const pageSchema = z.object({ tasks: z.array(taskSchema), partial: z.boolean() });
export default function AssistantTasks() {
  const internalCrm = process.env.REACT_APP_CRM_ENABLED === 'true' &&
    process.env.REACT_APP_CRM_ASSISTANT_ENABLED === 'true';
  const [tasks, setTasks] = useState<z.infer<typeof taskSchema>[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [partial, setPartial] = useState(false);
  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const page = pageSchema.parse(
        await assistantRequest('aksAssistantTasks', { action: 'list' })
      );
      setTasks(page.tasks);
      setPartial(page.partial);
    } catch (failure) {
      setTasks([]);
      setError(failure instanceof Error ? failure.message : 'Opgaverne kunne ikke hentes.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const complete = async (id: string) => {
    setBusy(id);
    setError('');
    try {
      await assistantRequest('aksAssistantTasks', { action: 'complete', id });
      setTasks((current) => current.filter((task) => task.id !== id));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Opgaven kunne ikke afsluttes.');
    } finally {
      setBusy(null);
    }
  };
  return (
    <Layout>
      <HeaderBar />
      <Layout.Content
        style={{ maxWidth: 900, width: '100%', margin: '24px auto', padding: '0 16px 100px' }}
      >
        <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Typography.Title level={2} style={{ margin: 0 }}>
            Mine AKS-opfølgninger
          </Typography.Title>
          <Button disabled={loading || Boolean(busy)} onClick={() => void refresh()}>
            Opdater
          </Button>
        </Space>
        <Typography.Paragraph type="secondary">
          {internalCrm ? (
            <>
              Tidligere AKS-opfølgninger kan afsluttes her. Nye salgs- og kundeopfølgninger
              findes under <Link to="/crm?kind=activity">Aktiviteter i CRM</Link>.
            </>
          ) : (
            <>Interne opgaver oprettet og godkendt via AKS-assistenten. Pipedrive-aktiviteter administreres i Pipedrive.</>
          )}
        </Typography.Paragraph>
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
        {partial && <Alert type="info" message="Viser de første 100 åbne opgaver." />}
        {loading ? (
          <Spin />
        ) : !tasks.length && !error ? (
          <Empty description="Ingen åbne AKS-opfølgninger" />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            {tasks.map((task) => (
              <Card key={task.id} size="small" title={task.subject}>
                <Typography.Paragraph>
                  {task.customerName} · {task.dueDate}
                </Typography.Paragraph>
                {task.note && (
                  <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                    {task.note}
                  </Typography.Paragraph>
                )}
                <Button
                  loading={busy === task.id}
                  disabled={Boolean(busy)}
                  onClick={() => void complete(task.id)}
                >
                  Markér udført
                </Button>
              </Card>
            ))}
          </Space>
        )}
      </Layout.Content>
      <FooterBar />
    </Layout>
  );
}
